/**
 * Text Field Type - Inline Renderers
 *
 * Read-only and editable in-table cell renderers for text columns.
 */

import { memo, useCallback } from 'react';
import type { ColumnDef, CellContext } from '@tanstack/react-table';
import { TableTooltip } from '../../TableTooltip';
import { useEditableContextOptional } from '../../OptimizedTableProvider';
import { SharedEditableCell } from './SharedEditableCell';
import { clearEditOnSuccess } from './inlineEditUtils';
import { CellInput } from 'features/misc/table';
import { CellContainer, TruncatedText } from '../../tableStyles';
import { toast } from 'react-toastify';
import { t } from '@lingui/macro';
import type { ColumnRenderer, RenderOptions, CustomColumn, EditableState } from '../../columnModel';
import { getCellStringValue } from '../cellValue';

// ============================================================================
// Read-Only Column Renderer
// ============================================================================

/**
 * Memoized read-only text cell component
 */
const TextReadOnlyCell = memo(function TextReadOnlyCell({ value }: { value: string }) {
  return (
    <TableTooltip title={value}>
      <CellContainer>
        <TruncatedText>{value}</TruncatedText>
      </CellContainer>
    </TableTooltip>
  );
});

/**
 * Text read-only renderer - displays text in a cell container with ellipsis for overflow
 * Uses getValue() to support nested accessors
 * Shows full text in tooltip on hover
 */
export const textReadOnlyRenderer = <T extends { id: number | string }>(
  _col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => ({
  cell: (cellContext: CellContext<T, unknown>) => {
    const value = cellContext.getValue();
    const displayValue = String(value ?? '');

    return <TextReadOnlyCell value={displayValue} />;
  },
  tooltipRender: (cell: CellContext<T, unknown>) => {
    const value = cell.getValue();
    return String(value ?? '');
  },
});

// ============================================================================
// Editable Column Renderer
// ============================================================================

/**
 * Memoized editable text cell component (reads editable state from context when inside table).
 */
type TextEditableCellProps<T extends { id: number | string }> = {
  context: CellContext<T, unknown>;
  value: string;
  col: CustomColumn<T>;
  editableRowIndex?: number | null;
  editableColumnId?: string | null;
  editableState?: EditableState | null;
  onEditCell?: (context: CellContext<T, unknown>) => void;
  onClearEdit?: () => void;
  isRowEditable: boolean;
};

const TextEditableCellInner = <T extends { id: number | string }>({
  context,
  value,
  col,
  editableRowIndex: editableRowIndexProp,
  editableColumnId: editableColumnIdProp,
  editableState: editableStateProp,
  onEditCell: onEditCellProp,
  onClearEdit: onClearEditProp,
  isRowEditable,
}: TextEditableCellProps<T>) => {
  const editableCtx = useEditableContextOptional();
  const editableState = editableStateProp ?? editableCtx?.editableState ?? null;
  const setEditableState = editableCtx?.setEditableState ?? (() => undefined);
  const editableRowIndex = editableRowIndexProp ?? editableState?.rowIndex ?? null;
  const editableColumnId = editableColumnIdProp ?? editableState?.columnId ?? null;
  const onEditCell =
    onEditCellProp ??
    (() => setEditableState({ rowIndex: context.row.index, columnId: context.column.id }));
  const onClearEdit =
    onClearEditProp ?? (() => setEditableState({ rowIndex: null, columnId: null }));

  const handleChange = useCallback(
    (newValue: string) => {
      // Auto-infer: use col.id as the field name
      if (col.onUpdate) {
        // Prevent clearing required fields
        if ((!newValue || newValue.trim() === '') && 'required' in col) {
          // Show validation error message
          toast.error(t`${col.label || 'This field'} is required and cannot be empty`);
          onClearEdit();
          return;
        }

        const result = col.onUpdate(context.row.original, {
          [col.id]: newValue,
        } as Partial<T>);
        clearEditOnSuccess(result, onClearEdit);
      } else {
        onClearEdit();
      }
    },
    [col, context.row.original, onClearEdit]
  );

  // If not editable for this row, render as read-only text with ellipsis
  if (!isRowEditable) {
    return <TruncatedText>{value}</TruncatedText>;
  }

  return (
    <SharedEditableCell
      {...context}
      meta={{ editable: true, onCellEdit: onEditCell }}
      isEditable={context.row.index === editableRowIndex && context.column.id === editableColumnId}
      tooltipTitle={editableState?.rowIndex != null ? undefined : value}
      readModeEl={<TruncatedText>{value}</TruncatedText>}
      onClearEdit={onClearEdit}
    >
      <CellInput value={value || ''} onChange={handleChange} />
    </SharedEditableCell>
  );
};

const TextEditableCell = memo(TextEditableCellInner) as <T extends { id: number | string }>(
  props: TextEditableCellProps<T>
) => JSX.Element;

/**
 * Text editable renderer - unified pattern
 * Uses col.id as the data field, auto-infers update
 * Supports conditional editability via editable: (cell) => boolean
 */
export const textEditableRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  const editableProp = col.editable;

  return {
    cell: (context: CellContext<T, unknown>) => {
      const value = getCellStringValue(context, col.id);
      const isRowEditable =
        typeof editableProp === 'function' ? editableProp(context) : editableProp === true;

      return (
        <TextEditableCell context={context} value={value} col={col} isRowEditable={isRowEditable} />
      );
    },
    meta: {
      editable: true,
    },
    tooltipRender: (cell: CellContext<T, unknown>) => {
      const value = cell.getValue();
      return String(value ?? '');
    },
  };
};

// ============================================================================
// Typed Renderer Exports (for registry)
// ============================================================================

/** Read-only renderer with proper typing */
export const textReadOnly: ColumnRenderer = textReadOnlyRenderer;

/** Editable renderer with proper typing */
export const textEditable: ColumnRenderer = textEditableRenderer;
